import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Phone, Mail, MapPin, Facebook, Instagram, Send,
} from "lucide-react";
import { getAttributionPayload } from "@/lib/attribution";
import OfficesMap from "@/components/landing/OfficesMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const attr = getAttributionPayload();
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        message: form.message,
        ...attr,
      });
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      setTimeout(() => setSent(false), 5000);
    } catch {
      alert("Nepodarilo sa odoslať. Skúste znovu alebo volajte 0907 64 99 64.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" className="py-24 lg:py-32 section-dark" data-testid="contact">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="mono text-xs text-[#00E5FF] uppercase tracking-[0.2em]">
              Kontakt
            </span>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight mt-4 mb-6">
              Poďme <span className="text-[#00E5FF]">stavať</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-10 max-w-md">
              Ozvite sa nám a do 24 hodín vám pripravíme nezáväznú cenovú ponuku.
            </p>

            <div className="space-y-6">
              <a
                href="tel:+421907649964"
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 bg-[#00E5FF] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone size={24} className="text-black" />
                </div>
                <div>
                  <p className="text-zinc-500 text-sm">Telefón</p>
                  <p className="text-xl font-bold mono">0907 64 99 64</p>
                </div>
              </a>

              <a
                href="mailto:supermajstri.sk@gmail.com"
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center group-hover:bg-[#00E5FF] group-hover:text-black transition-all">
                  <Mail size={24} />
                </div>
                <div>
                  <p className="text-zinc-500 text-sm">Email</p>
                  <p className="text-xl font-bold">supermajstri.sk@gmail.com</p>
                </div>
              </a>

              <a
                href="#kontakt"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('[data-testid="offices-map"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="flex items-center gap-4 group"
                data-testid="contact-address"
              >
                <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center group-hover:bg-[#00E5FF] group-hover:text-black transition-all">
                  <MapPin size={24} />
                </div>
                <div>
                  <p className="text-zinc-500 text-sm">Naše pobočky</p>
                  <p className="text-xl font-bold">Bratislava · Lehnice</p>
                </div>
              </a>
            </div>

            <div className="flex gap-4 mt-10">
              <a
                href="https://facebook.com/supermajstri.sk"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center hover:bg-[#00E5FF] hover:text-black transition-all"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://instagram.com/supermajstri"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center hover:bg-[#00E5FF] hover:text-black transition-all"
              >
                <Instagram size={20} />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form
              onSubmit={handleSubmit}
              className="glass-card p-8"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Meno *"
                  required
                  aria-label="Meno"
                  autoComplete="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-zinc-500 focus:border-[#00E5FF]"
                  data-testid="form-name"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  required
                  aria-label="Email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-zinc-500 focus:border-[#00E5FF]"
                  data-testid="form-email"
                />
              </div>
              <input
                type="tel"
                placeholder="Telefón"
                aria-label="Telefón"
                autoComplete="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-zinc-500 focus:border-[#00E5FF] mb-4"
                data-testid="form-phone"
              />
              <textarea
                placeholder="Správa *"
                aria-label="Správa"
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-zinc-500 focus:border-[#00E5FF] mb-6 resize-none"
                data-testid="form-message"
              />

              <button
                type="submit"
                disabled={sending}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="form-submit"
              >
                {sending ? "Odosielam…" : sent ? "Odoslané!" : (
                  <>
                    Odoslať <Send size={18} />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>

        <OfficesMap />
      </div>
    </section>
  );
}
